import { 
  collection, 
  query, 
  where, 
  orderBy, 
  onSnapshot, 
  doc, 
  updateDoc, 
  addDoc, 
  serverTimestamp, 
  getDocs,
  getDoc 
} from 'firebase/firestore';
import { toast } from 'react-toastify';
import { logHelpfulError, isIndexError, isPermissionError } from '../utils/firebaseIndexHelper.js';

/**
 * Task Automation Service - Chunk 3 Implementation
 * Handles intelligent task management, auto-escalation, and smart actions
 */

class TaskAutomationService {
  constructor(db) {
    this.db = db;
    this.activeMonitors = new Map();
    this.writePermissionCache = new Map(); // Cache write permission status
    this.escalationRules = {
      critical: {
        firstAlert: 30, // minutes before deadline
        escalation: 15, // minutes before deadline
        finalAlert: 5   // minutes before deadline
      },
      important: {
        firstAlert: 120, // 2 hours
        escalation: 60,  // 1 hour
        finalAlert: 30   // 30 minutes
      },
      standard: {
        firstAlert: 360, // 6 hours
        escalation: 120, // 2 hours
        finalAlert: 60   // 1 hour
      }
    };
  }

  /**
   * Check if we have write permissions for a collection
   */
  async checkWritePermission(collectionName) {
    // Check cache first
    const cacheKey = collectionName;
    if (this.writePermissionCache.has(cacheKey)) {
      return this.writePermissionCache.get(cacheKey);
    }

    try {
      // Try a simple write operation to test permissions
      const testRef = collection(this.db, collectionName);
      await addDoc(testRef, {
        test: true,
        timestamp: serverTimestamp()
      });
      
      // If successful, cache the result
      this.writePermissionCache.set(cacheKey, true);
      return true;
    } catch (error) {
      // If failed, cache the result and return false
      this.writePermissionCache.set(cacheKey, false);
      return false;
    }
  }

  /**
   * Safe write operation with permission checking
   */
  async safeAddDoc(collectionRef, data) {
    const collectionName = collectionRef.path.split('/')[0];
    
    // Check if we have write permissions
    const hasPermission = await this.checkWritePermission(collectionName);
    if (!hasPermission) {
      console.group('🔒 Write Permission Notice');
      console.warn(`Skipping write to ${collectionName} - insufficient permissions`);
      console.info('This is expected behavior for certain user roles.');
      console.groupEnd();
      return null;
    }

    try {
      return await addDoc(collectionRef, data);
    } catch (error) {
      // Cache permission failure for future calls
      this.writePermissionCache.set(collectionName, false);
      logHelpfulError(error, `Task Automation Service - safeAddDoc (${collectionName})`);
      return null;
    }
  }

  /**
   * Start monitoring tasks for an incident
   */
  async startTaskMonitoring(incidentId, orgId) {
    if (this.activeMonitors.has(incidentId)) {
      console.log(`Task monitoring already active for incident ${incidentId}`);
      return;
    }

    console.log(`Starting task automation monitoring for incident ${incidentId}`);

    const tasksRef = collection(this.db, 'c3_tasks');
    const q = query(
      tasksRef,
      where('incidentId', '==', incidentId),
      where('orgId', '==', orgId),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const tasks = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      this.processTasks(tasks, incidentId, orgId);
    });

    this.activeMonitors.set(incidentId, unsubscribe);
  }

  /**
   * Stop monitoring tasks for an incident
   */
  stopTaskMonitoring(incidentId) {
    const unsubscribe = this.activeMonitors.get(incidentId);
    if (unsubscribe) {
      unsubscribe();
      this.activeMonitors.delete(incidentId);
      console.log(`Stopped task monitoring for incident ${incidentId}`);
    }
  }

  /**
   * Process tasks and trigger automation actions
   */
  async processTasks(tasks, incidentId, orgId) {
    const now = new Date();
    
    // Check for escalation needs
    await this.checkEscalations(tasks, now, incidentId, orgId);
    
    // Check for workload imbalances
    await this.checkWorkloadBalance(tasks, incidentId, orgId);
    
    // Check for missing critical tasks
    await this.checkMissingTasks(tasks, incidentId, orgId);
    
    // Check for communication gaps
    await this.checkCommunicationGaps(tasks, incidentId, orgId);
  }

  /**
   * Check tasks for escalation needs
   */
  async checkEscalations(tasks, now, incidentId, orgId) {
    for (const task of tasks) {
      if (task.status === 'completed' || !task.dueDate) continue;

      const dueDate = task.dueDate.toDate ? task.dueDate.toDate() : new Date(task.dueDate);
      const minutesUntilDue = Math.floor((dueDate - now) / (1000 * 60));
      const priority = this.getTaskPriority(task);
      const rules = this.escalationRules[priority];

      // Check if task needs escalation
      if (minutesUntilDue <= rules.escalation && minutesUntilDue > 0) {
        await this.escalateTask(task, incidentId, orgId, minutesUntilDue);
      }
      
      // Check for first alert
      else if (minutesUntilDue <= rules.firstAlert && minutesUntilDue > rules.escalation) {
        await this.sendTaskAlert(task, incidentId, orgId, 'first_alert', minutesUntilDue);
      }
      
      // Check for final alert
      else if (minutesUntilDue <= rules.finalAlert && minutesUntilDue > 0) {
        await this.sendTaskAlert(task, incidentId, orgId, 'final_alert', minutesUntilDue);
      }
      
      // Check for overdue tasks
      else if (minutesUntilDue < 0) {
        await this.handleOverdueTask(task, incidentId, orgId, Math.abs(minutesUntilDue));
      }
    }
  }

  /**
   * Escalate task to CMT Leader
   */
  async escalateTask(task, incidentId, orgId, minutesUntilDue) {
    // Check if already escalated recently
    if (task.lastEscalated && (new Date() - task.lastEscalated.toDate()) < 300000) { // 5 minutes
      return;
    }

    console.log(`Escalating task ${task.id} - ${minutesUntilDue} minutes until deadline`);

    try {
      // Update task with escalation info
      const taskRef = doc(this.db, 'c3_tasks', task.id);
      await updateDoc(taskRef, {
        escalated: true,
        escalatedAt: serverTimestamp(),
        lastEscalated: serverTimestamp(),
        escalationReason: `Task due in ${minutesUntilDue} minutes - auto-escalated to CMT Leader`
      });

      // Create escalation notification
      await this.createEscalationNotification(task, incidentId, orgId, minutesUntilDue);

      // Log escalation activity
      await this.logActivity(incidentId, orgId, 'task_escalated', {
        taskId: task.id,
        taskName: task.name,
        originalAssignee: task.assignedRole,
        minutesUntilDue: minutesUntilDue,
        escalatedTo: 'cmt_leader'
      });

    } catch (error) {
      logHelpfulError(error, 'Task Automation Service - escalateTask');
    }
  }

  /**
   * Send task alert to assignee
   */
  async sendTaskAlert(task, incidentId, orgId, alertType, minutesUntilDue) {
    // Check if alert already sent recently
    const alertKey = `${alertType}_${task.id}`;
    if (task[`${alertType}_sent`]) return;

    console.log(`Sending ${alertType} for task ${task.id} - ${minutesUntilDue} minutes until deadline`);

    try {
      // Update task to mark alert as sent
      const taskRef = doc(this.db, 'c3_tasks', task.id);
      await updateDoc(taskRef, {
        [`${alertType}_sent`]: serverTimestamp(),
        [`${alertType}_minutes`]: minutesUntilDue
      });

      // Create alert notification
      await this.createTaskAlert(task, incidentId, orgId, alertType, minutesUntilDue);

    } catch (error) {
      logHelpfulError(error, 'Task Automation Service - sendTaskAlert');
    }
  }

  /**
   * Handle overdue tasks
   */
  async handleOverdueTask(task, incidentId, orgId, minutesOverdue) {
    if (task.overdueHandled) return;

    console.log(`Handling overdue task ${task.id} - ${minutesOverdue} minutes overdue`);

    try {
      // Mark task as overdue
      const taskRef = doc(this.db, 'c3_tasks', task.id);
      await updateDoc(taskRef, {
        overdue: true,
        overdueAt: serverTimestamp(),
        overdueHandled: true,
        minutesOverdue: minutesOverdue
      });

      // Create damage control tasks if critical
      if (this.getTaskPriority(task) === 'critical') {
        await this.createDamageControlTasks(task, incidentId, orgId, minutesOverdue);
      }

      // Log overdue activity
      await this.logActivity(incidentId, orgId, 'task_overdue', {
        taskId: task.id,
        taskName: task.name,
        assignedRole: task.assignedRole,
        minutesOverdue: minutesOverdue,
        priority: this.getTaskPriority(task)
      });

    } catch (error) {
      logHelpfulError(error, 'Task Automation Service - handleOverdueTask');
    }
  }

  /**
   * Check workload balance across CMT members
   */
  async checkWorkloadBalance(tasks, incidentId, orgId) {
    const activeTasks = tasks.filter(t => t.status !== 'completed');
    const workloadByRole = {};

    // Calculate workload per role
    activeTasks.forEach(task => {
      const role = task.assignedRole || 'unassigned';
      if (!workloadByRole[role]) {
        workloadByRole[role] = { count: 0, tasks: [] };
      }
      workloadByRole[role].count++;
      workloadByRole[role].tasks.push(task);
    });

    // Find imbalances
    const roles = Object.keys(workloadByRole);
    const maxLoad = Math.max(...roles.map(role => workloadByRole[role].count));
    const minLoad = Math.min(...roles.map(role => workloadByRole[role].count));

    // If imbalance is significant (difference > 3 tasks)
    if (maxLoad - minLoad > 3) {
      await this.suggestWorkloadRebalancing(workloadByRole, incidentId, orgId);
    }
  }

  /**
   * Check for missing critical tasks based on incident type
   */
  async checkMissingTasks(tasks, incidentId, orgId) {
    try {
      // Get incident details
      const incidentRef = doc(this.db, 'c3_incidents', incidentId);
      const incidentDoc = await getDoc(incidentRef);
      
      if (!incidentDoc.exists()) return;
      
      const incident = incidentDoc.data();
      const crisisType = incident.crisisType;
      
      // Get expected tasks for this crisis type
      const expectedTasks = await this.getExpectedTasksForCrisisType(crisisType);
      const existingTaskNames = tasks.map(t => t.name.toLowerCase());
      
      // Find missing critical tasks
      const missingTasks = expectedTasks.filter(expected => 
        expected.priority === 'critical' && 
        !existingTaskNames.some(existing => 
          existing.includes(expected.name.toLowerCase()) || 
          expected.name.toLowerCase().includes(existing)
        )
      );

      if (missingTasks.length > 0) {
        await this.suggestMissingTasks(missingTasks, incidentId, orgId, crisisType);
      }

    } catch (error) {
      logHelpfulError(error, 'Task Automation Service - checkMissingTasks');
      
      // Handle permission errors gracefully
      if (isPermissionError(error)) {
        return;
      }
    }
  }

  /**
   * Check for communication gaps
   */
  async checkCommunicationGaps(tasks, incidentId, orgId) {
    const now = new Date();
    const fourHoursAgo = new Date(now - 4 * 60 * 60 * 1000);

    try {
      // Check for recent stakeholder communications
      const updatesRef = collection(this.db, 'c3_updates');
      const updatesQuery = query(
        updatesRef,
        where('incidentId', '==', incidentId),
        where('orgId', '==', orgId),
        where('timestamp', '>=', fourHoursAgo)
      );
      
      const updatesSnapshot = await getDocs(updatesQuery);
      const recentUpdates = updatesSnapshot.docs.length;

      // If no updates in 4 hours, suggest communication task
      if (recentUpdates === 0) {
        await this.suggestCommunicationTask(incidentId, orgId);
      }

    } catch (error) {
      logHelpfulError(error, 'Task Automation Service - checkCommunicationGaps');
      
      // If it's an index error, skip this check gracefully
      if (isIndexError(error)) {
        return;
      }
    }
  }

  /**
   * Get task priority level
   */
  getTaskPriority(task) {
    const priority = task.priority?.toLowerCase() || 'standard';
    
    // Map various priority formats to our standard levels
    if (['critical', 'urgent', 'immediate'].includes(priority)) return 'critical';
    if (['important', 'high', 'recommended'].includes(priority)) return 'important';
    return 'standard';
  }

  /**
   * Create escalation notification
   */
  async createEscalationNotification(task, incidentId, orgId, minutesUntilDue) {
    try {
      const notificationsRef = collection(this.db, 'c3_notifications');
      
      await this.safeAddDoc(notificationsRef, {
        incidentId,
        orgId,
        type: 'task_escalation',
        priority: 'urgent',
        targetRole: 'cmt_leader',
        title: `URGENT: Task Escalation Required`,
        message: `Task "${task.name}" is due in ${minutesUntilDue} minutes and needs immediate attention.`,
        taskId: task.id,
        originalAssignee: task.assignedRole,
        minutesUntilDue,
        createdAt: serverTimestamp(),
        read: false,
        actionRequired: true,
        suggestedActions: [
          'Reassign to available team member',
          'Extend deadline if appropriate',
          'Mark as completed if done',
          'Escalate to senior management'
        ]
      });
    } catch (error) {
      logHelpfulError(error, 'Task Automation Service - createEscalationNotification');
      // Continue gracefully if notification creation fails
    }
  }

  /**
   * Create task alert notification
   */
  async createTaskAlert(task, incidentId, orgId, alertType, minutesUntilDue) {
    try {
      const notificationsRef = collection(this.db, 'c3_notifications');
      
      const alertMessages = {
        first_alert: `Task "${task.name}" is due in ${minutesUntilDue} minutes. Please update status.`,
        final_alert: `URGENT: Task "${task.name}" is due in ${minutesUntilDue} minutes!`
      };

      await this.safeAddDoc(notificationsRef, {
        incidentId,
        orgId,
        type: 'task_alert',
        priority: alertType === 'final_alert' ? 'urgent' : 'normal',
        targetRole: task.assignedRole,
        title: alertType === 'final_alert' ? 'URGENT: Task Due Soon' : 'Task Reminder',
        message: alertMessages[alertType],
        taskId: task.id,
        minutesUntilDue,
        createdAt: serverTimestamp(),
        read: false
      });
    } catch (error) {
      logHelpfulError(error, 'Task Automation Service - createTaskAlert');
      // Continue gracefully if notification creation fails
    }
  }

  /**
   * Log automation activity
   */
  async logActivity(incidentId, orgId, activityType, details) {
    try {
      const activitiesRef = collection(this.db, 'c3_activities');
      await this.safeAddDoc(activitiesRef, {
        incidentId,
        orgId,
        type: activityType,
        source: 'automation',
        details,
        timestamp: serverTimestamp()
      });
    } catch (error) {
      logHelpfulError(error, 'Task Automation Service - logActivity');
      // Continue gracefully if activity logging fails
    }
  }

  /**
   * Suggest workload rebalancing
   */
  async suggestWorkloadRebalancing(workloadByRole, incidentId, orgId) {
    try {
      const roles = Object.keys(workloadByRole);
      const overloadedRole = roles.reduce((max, role) => 
        workloadByRole[role].count > workloadByRole[max].count ? role : max
      );
      const underloadedRole = roles.reduce((min, role) => 
        workloadByRole[role].count < workloadByRole[min].count ? role : min
      );

      const notificationsRef = collection(this.db, 'c3_notifications');
      
      await this.safeAddDoc(notificationsRef, {
        incidentId,
        orgId,
        type: 'workload_imbalance',
        priority: 'normal',
        targetRole: 'cmt_leader',
        title: 'Workload Imbalance Detected',
        message: `${overloadedRole} has ${workloadByRole[overloadedRole].count} tasks while ${underloadedRole} has ${workloadByRole[underloadedRole].count}. Consider redistributing tasks.`,
        overloadedRole,
        underloadedRole,
        suggestedTasks: workloadByRole[overloadedRole].tasks.slice(0, 2).map(t => ({
          id: t.id,
          name: t.name,
          priority: t.priority
        })),
        createdAt: serverTimestamp(),
        read: false,
        actionRequired: true
      });
    } catch (error) {
      logHelpfulError(error, 'Task Automation Service - suggestWorkloadRebalancing');
      // Continue gracefully if notification creation fails
    }
  }

  /**
   * Get expected tasks for crisis type (simplified version)
   */
  async getExpectedTasksForCrisisType(crisisType) {
    // This would typically load from the crisis task templates
    // For now, return common critical tasks
    const commonCriticalTasks = [
      { name: 'Notify authorities', priority: 'critical' },
      { name: 'Ensure staff safety', priority: 'critical' },
      { name: 'Activate crisis team', priority: 'critical' },
      { name: 'Assess situation', priority: 'critical' }
    ];

    return commonCriticalTasks;
  }

  /**
   * Suggest missing critical tasks
   */
  async suggestMissingTasks(missingTasks, incidentId, orgId, crisisType) {
    try {
      const notificationsRef = collection(this.db, 'c3_notifications');
      
      await this.safeAddDoc(notificationsRef, {
        incidentId,
        orgId,
        type: 'missing_tasks',
        priority: 'important',
        targetRole: 'cmt_leader',
        title: 'Missing Critical Tasks Detected',
        message: `Based on ${crisisType} incident type, the following critical tasks may be missing: ${missingTasks.map(t => t.name).join(', ')}`,
        crisisType,
        missingTasks,
        createdAt: serverTimestamp(),
        read: false,
        actionRequired: true,
        suggestedActions: ['Create missing tasks', 'Review task list', 'Confirm tasks not needed']
      });
    } catch (error) {
      logHelpfulError(error, 'Task Automation Service - suggestMissingTasks');
      // Continue gracefully if notification creation fails
    }
  }

  /**
   * Suggest communication task
   */
  async suggestCommunicationTask(incidentId, orgId) {
    try {
      const notificationsRef = collection(this.db, 'c3_notifications');
      
      await this.safeAddDoc(notificationsRef, {
        incidentId,
        orgId,
        type: 'communication_gap',
        priority: 'important',
        targetRole: 'communications',
        title: 'Communication Gap Detected',
        message: 'No stakeholder updates have been sent in the last 4 hours. Consider sending a status update.',
        createdAt: serverTimestamp(),
        read: false,
        actionRequired: true,
        suggestedActions: [
          'Send stakeholder update',
          'Prepare media statement',
          'Update internal communications',
          'Schedule next communication'
        ]
      });
    } catch (error) {
      logHelpfulError(error, 'Task Automation Service - suggestCommunicationTask');
      // Continue gracefully if notification creation fails
    }
  }

  /**
   * Create damage control tasks for overdue critical items
   */
  async createDamageControlTasks(overdueTask, incidentId, orgId, minutesOverdue) {
    try {
      const tasksRef = collection(this.db, 'c3_tasks');
      
      // Create damage assessment task
      await this.safeAddDoc(tasksRef, {
        name: `Assess Impact of Missed Deadline: ${overdueTask.name}`,
        description: `Evaluate the consequences of missing the deadline for "${overdueTask.name}" by ${minutesOverdue} minutes. Determine if immediate corrective action is needed.`,
        incidentId,
        orgId,
        assignedRole: 'cmt_leader',
        priority: 'critical',
        status: 'not_started',
        type: 'damage_control',
        relatedTaskId: overdueTask.id,
        createdAt: serverTimestamp(),
        createdBy: 'automation',
        source: 'auto_damage_control'
      });
    } catch (error) {
      logHelpfulError(error, 'Task Automation Service - createDamageControlTasks');
      // Continue gracefully if task creation fails
    }
  }
}

export default TaskAutomationService; 